import React, { useState } from "react";
import { Button, Select, Tooltip } from "antd";
import { FullscreenOutlined, CloseOutlined } from "@ant-design/icons";
import AssemblyMap from "../AssemblyMap";
import VillageDataTable from "./VillageDataTable";
import GraphModal from "../GraphModal";
import sampleVillageData from "./sampleVillageData.json";
import { getGraphData } from "../utils/getFromattedValue";
import { renderCharts } from "../utils/renderCharts";
import ExpandIcon from "../../../assets/svg/expandIcon.svg";
import { useTranslation } from "react-i18next";

const VillageData = ({
  constituencyData,
  summaryData,
  polygonData,
  constituencyDataList,
  center,
  setSelectedMarker = () => {},
  villageFetchedData,
  prevVillageFetchedData,
  isDataAvailable,
  selectedMarker,
  isLoading,
  electionYears,
  setSelectedYear,
  constituencyId,
  year,
  setYear,
  previousYear,
  setPreviousYear,
  storedConstituencyData,
}) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState("age");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));

  const openModal = () => setIsModalVisible(true);
  const closeModal = () => setIsModalVisible(false);
  const toggleFullScreen = () => setIsFullScreen(!isFullScreen);
  const handleSelectChange = (value) => setSelectedOption(value);

  const graphData = getGraphData(
    selectedOption,
    sampleVillageData,
    villageFetchedData
  );

  return (
    <>
      <div>
        {/* Section Header */}
        <div className="flex justify-center items-center mb-4">
          <h4 className="text-3xl font-bold text-gray-800">
            {user?.urbanRural === "Urban" ? t("Ward Data") : t("villageData")}
          </h4>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Map Section */}
          <div className="lg:col-span-3 h-full">
            <div
              className={`transition-all duration-300 transform hover:scale-[1.01] text-black p-4 relative rounded-2xl border border-white/30 bg-gray-300/20 h-[100%] ${
                isFullScreen ? "" : "cdm-village-items-responsive map-responsive h-[830px]"
              }`}
            >
              {/* Fullscreen Toggle */}
              <div className="absolute top-8 left-8 z-20">
                <Tooltip title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"}>
                  <Button
                    icon={isFullScreen ? <CloseOutlined /> : <FullscreenOutlined />}
                    onClick={toggleFullScreen}
                    shape="circle"
                    size="large"
                    className="bg-white/90 backdrop-blur-sm border-none shadow-lg hover:bg-white/95"
                    disabled={!constituencyDataList || constituencyDataList.length === 0}
                  />
                </Tooltip>
              </div>

              <AssemblyMap
                polygonData={constituencyData}
                center={center}
                storedConstituencyData={storedConstituencyData}
                setSelectedMarker={setSelectedMarker}
                selectedMarker={selectedMarker}
                isDataAvailable={isDataAvailable}
                villageFetchedData={villageFetchedData?.electoralData}
                prevVillageFetchedData={prevVillageFetchedData}
                isFullScreen={isFullScreen}
                setIsFullScreen={setIsFullScreen}
                onExitFullScreen={() => setIsFullScreen(false)}
                electionYears={electionYears}
                setSelectedYear={setSelectedYear}
                constituencyId={constituencyId}
                year={year}
                setYear={setYear}
                previousYear={previousYear}
                setPreviousYear={setPreviousYear}
              />
            </div>
          </div>

          {/* Data Section */}
          <div className="lg:col-span-2 space-y-4">
            <VillageDataTable
              isDataAvailable={isDataAvailable}
              data={villageFetchedData?.electoralData}
              selectedMarker={selectedMarker}
              villageLoading={isLoading}
            />

            {/* Electoral Data Chart Card */}
            <div className="card-wrapper">
              <div className="flex justify-between items-center mb-2 relative z-10">
                <h4 className="text-lg font-bold text-black">
                  {user?.urbanRural === "Urban"
                    ? t("Ward Electoral Data")
                    : t("Village Electoral Data")}
                </h4>
                <div className="flex items-center gap-3">
                  <Select
                    placeholder="Select Data Type"
                    value={selectedOption}
                    onChange={handleSelectChange}
                    disabled={!isDataAvailable}
                    className="min-w-[140px] modern-select"
                    size="middle"
                  >
                    <Select.Option value="age">{t("age")}</Select.Option>
                    <Select.Option value="gender">{t("gender")}</Select.Option>
                    <Select.Option value="ageGender">{t("ageVsGender")}</Select.Option>
                    <Select.Option value="community">{t("community")}</Select.Option>
                  </Select>
                  <button
                    onClick={openModal}
                    className="p-2.5 hover:bg-white/30 rounded-xl transition-all duration-200 backdrop-blur-sm border border-white/20 shadow-lg hover:shadow-xl hover:scale-105 bg-white/20"
                    disabled={!isDataAvailable || selectedOption === "community"}
                  >
                    <img src={ExpandIcon} alt="expand" className="w-4 h-4 opacity-80" />
                  </button>
                </div>
              </div>

              <div className="h-[320px] relative z-10 bg-gray-400/10 backdrop-blur-sm rounded-2xl px-4 border border-white/20">
                {renderCharts(selectedOption, isDataAvailable, graphData, isLoading)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fullscreen Map Modal */}
      {isFullScreen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="relative w-[95%] h-[90%] bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Close Button */}
            <button
              onClick={toggleFullScreen}
              className="absolute top-4 right-4 z-50 p-2 bg-white rounded-full shadow hover:scale-105 transition"
            >
              <CloseOutlined />
            </button>

            <AssemblyMap
              polygonData={constituencyData}
              center={center}
              storedConstituencyData={storedConstituencyData}
              setSelectedMarker={setSelectedMarker}
              selectedMarker={selectedMarker}
              isDataAvailable={isDataAvailable}
              villageFetchedData={villageFetchedData?.electoralData}
              prevVillageFetchedData={prevVillageFetchedData}
              isFullScreen={isFullScreen}
              setIsFullScreen={setIsFullScreen}
              onExitFullScreen={() => setIsFullScreen(false)}
              electionYears={electionYears}
              setSelectedYear={setSelectedYear}
              constituencyId={constituencyId}
              year={year}
              setYear={setYear}
              previousYear={previousYear}
              setPreviousYear={setPreviousYear}
            />
          </div>
        </div>
      )}

      {/* Graph Modal */}
      <GraphModal
        isDataAvailable={isDataAvailable}
        isModalVisible={isModalVisible}
        closeModal={closeModal}
        graphData={graphData}
        selectedOption={selectedOption}
      />
    </>
  );
};

export default VillageData;
